"use client";

import { useState, useEffect, Suspense, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useCartStore } from "../store/cartStore";

function SearchContent() {
  const searchParams = useSearchParams();
  
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);

  const [brands, setBrands] = useState<any[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>("");
  
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [exchangeRate, setExchangeRate] = useState<number>(45.0);

  const addToCart = useCartStore((state) => state.addToCart);

  useEffect(() => {
    fetch("https://api.autologistic.com.ua/api/brands", { cache: 'no-store' })
      .then((res) => res.json())
      .then((data) => setBrands(data))
      .catch((err) => console.error("Помилка завантаження брендів:", err));

    const initialQuery = searchParams.get("q") || "";
    const initialBrandId = searchParams.get("brand_id") || "";

    if (initialQuery) setQuery(initialQuery);
    if (initialBrandId) setSelectedBrand(initialBrandId);

    if (initialQuery || initialBrandId) {
      executeSearch(initialQuery, initialBrandId);
    }
  }, [searchParams]);

  const executeSearch = async (searchQuery: string, brandId: string) => {
    if (searchQuery.trim().length < 2 && !brandId) return;

    setLoading(true);
    setSearched(true);

    try {
      const url = new URL("https://api.autologistic.com.ua/api/products/search");
      if (searchQuery.trim().length >= 2) url.searchParams.append("q", searchQuery.trim());
      if (brandId) url.searchParams.append("brand_id", brandId);

      const res = await fetch(url.toString(), { cache: 'no-store' });
      const data = await res.json();

      if (data.status === "success") {
        setResults(data.results);
        if (data.exchange_rate) setExchangeRate(data.exchange_rate);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error("Помилка під час пошуку:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    executeSearch(query, selectedBrand);
  };

  const handleBrandClick = (brandId: string) => {
    setSelectedBrand(brandId);
    executeSearch(query, brandId);
  };

  const filteredResults = inStockOnly ? results.filter(item => item.stock > 0) : results;

  const groupedResults = useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    filteredResults.forEach(item => {
      const model = (item.model && item.model !== "nan") ? item.model : "Інші (Модель не вказана)";
      if (!groups[model]) groups[model] = [];
      groups[model].push(item);
    });
    return Object.keys(groups).sort().reduce((acc: any, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, [filteredResults]);

  useEffect(() => {
    const initialState: Record<string, boolean> = {};
    Object.keys(groupedResults).forEach(key => {
      initialState[key] = true;
    });
    setExpandedGroups(initialState);
  }, [groupedResults]);

  const toggleGroup = (modelName: string) => {
    setExpandedGroups(prev => ({ ...prev, [modelName]: !prev[modelName] }));
  };

  const toggleAll = (expand: boolean) => {
    const newState: Record<string, boolean> = {};
    Object.keys(groupedResults).forEach(key => { newState[key] = expand; });
    setExpandedGroups(newState);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        
        {/* КАТАЛОГ */}
        <aside className="hidden lg:block">
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm sticky top-6">
            <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="font-bold text-gray-800 uppercase tracking-wide">Виробники</h2>
            </div>
            <ul className="max-h-[75vh] overflow-y-auto divide-y divide-gray-100">
              <li>
                <button
                  onClick={() => handleBrandClick("")}
                  className={`w-full text-left px-6 py-4 text-sm font-bold transition-colors hover:bg-blue-50 ${!selectedBrand ? 'text-blue-700 bg-blue-50 border-l-4 border-blue-600' : 'text-gray-700 border-l-4 border-transparent'}`}
                >
                  Усі бренди
                </button>
              </li>
              {brands.map((b) => (
                <li key={b.id}>
                  <button
                    onClick={() => handleBrandClick(b.id.toString())}
                    className={`w-full text-left px-6 py-3 text-sm font-medium transition-colors hover:bg-blue-50 ${selectedBrand === b.id.toString() ? 'text-blue-700 bg-blue-50 border-l-4 border-blue-600' : 'text-gray-600 border-l-4 border-transparent'}`}
                  >
                    {b.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* ОСНОВНА ЗОНА */}
        <div>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm mb-8 border border-gray-100">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Каталог та пошук</h1>
            <p className="text-gray-500 mb-6 text-sm">Введіть артикул, OE номер або назву деталі.</p>

            <div className="lg:hidden mb-4">
              <select
                value={selectedBrand}
                onChange={(e) => handleBrandClick(e.target.value)}
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-gray-700 bg-white"
              >
                <option value="">Усі бренди (Каталог)</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id.toString()}>{b.name}</option>
                ))}
              </select>
            </div>

            <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Наприклад: TO80010001, 06H109021K..."
                  className="flex-1 px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <button
                  type="submit"
                  disabled={loading || (query.trim().length < 2 && !selectedBrand)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-xl disabled:bg-blue-300"
                >
                  {loading ? "Шукаємо..." : "Знайти"}
                </button>
              </div>
              <div className="flex items-center gap-3 mt-2 border-t border-gray-100 pt-4">
                <label className="flex items-center cursor-pointer relative group">
                  <input type="checkbox" className="sr-only" checked={inStockOnly} onChange={() => setInStockOnly(!inStockOnly)} />
                  <div className={`w-11 h-6 rounded-full transition-colors ${inStockOnly ? 'bg-green-500' : 'bg-gray-200 group-hover:bg-gray-300'}`}>
                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${inStockOnly ? 'translate-x-5' : ''}`}></div>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700 select-none">Показувати тільки в наявності</span>
                </label>
              </div>
            </form>
          </div>

          {searched && !loading && filteredResults.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100 mb-8">
              <p className="text-xl text-gray-500">За цим запитом нічого не знайдено 😔</p>
            </div>
          )}

          {Object.keys(groupedResults).length > 1 && (
            <div className="flex justify-end mb-4">
              <button
                onClick={() => {
                  const allExpanded = Object.values(expandedGroups).every(v => v);
                  toggleAll(!allExpanded);
                }}
                className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1 bg-blue-50 px-4 py-2 rounded-lg"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16"></path>
                </svg>
                {Object.values(expandedGroups).every(v => v) ? "Згорнути всі групи" : "Розгорнути всі групи"}
              </button>
            </div>
          )}

          {Object.entries(groupedResults).map(([modelName, items]: [string, any]) => (
            <div key={modelName} className="mb-6 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              
              <button
                onClick={() => toggleGroup(modelName)}
                className="w-full flex items-center justify-between p-5 bg-gray-50/50 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <h2 className="text-xl md:text-2xl font-black text-gray-800 px-2 border-l-4 border-[#ff6600] text-left">
                    {modelName}
                  </h2>
                  <span className="bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1 rounded-full">
                    {items.length} шт
                  </span>
                </div>
                <div className={`p-2 rounded-full transition-transform duration-300 ${expandedGroups[modelName] ? 'rotate-180 bg-gray-200' : 'bg-gray-100'}`}>
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </button>

              {expandedGroups[modelName] && (
                <div className="p-5 grid grid-cols-1 gap-4 border-t border-gray-100">
                  {items.map((item: any, index: number) => (
                    <div key={index} className="bg-white p-5 rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-gray-100 text-gray-700 text-xs font-bold px-3 py-1 rounded-full uppercase">{item.brand}</span>
                          <span className="text-sm font-mono text-gray-500 border-b border-dashed border-gray-300">{item.sku}</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2">{item.name}</h3>
                        {item.oe_numbers && item.oe_numbers.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            <span className="text-xs text-gray-400 font-medium py-1">OE:</span>
                            {item.oe_numbers.map((oe: string, i: number) => (
                              <span key={i} className="bg-blue-50 text-blue-700 border border-blue-100 text-xs px-2 py-1 rounded-md font-mono">{oe}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="lg:text-right lg:border-l lg:pl-6 border-gray-100 flex flex-row lg:flex-col justify-between items-center lg:items-end">
                        
                        <div className="flex flex-col items-end mb-1">
                          <div className="text-2xl font-black text-gray-900 leading-none">
                            {(item.price_uah || 0).toFixed(0)} ₴
                          </div>
                          <div className="text-sm font-bold text-gray-400 mt-1">
                            {(item.price_usd || 0).toFixed(2)} $
                          </div>
                        </div>
                        
                        {item.stock > 0 ? (
                          <div className="text-green-600 text-sm font-semibold flex items-center gap-1 mt-2 lg:mt-0">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span> 
                            В наявності ({item.stock > 5 ? '> 5' : item.stock} шт)
                          </div>
                        ) : (
                          <div className="text-red-500 text-sm font-semibold flex items-center gap-1 mt-2 lg:mt-0">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span> 
                            Під замовлення
                          </div>
                        )}
                   
                        <button 
                          onClick={() => addToCart({
                            brand: item.brand,
                            article: item.sku,
                            name: item.name,
                            quantity: 1,
                            price_uah: item.price_uah || 0,
                            price_usd: item.price_usd || 0.0,
                            buy_price_usd: item.buy_price_usd || 0.0,
                            base_currency: item.base_currency || 'USD',
                            root_supplier: item.root_supplier || 'Техномир',
                            supplier_code: item.supplier_code || '',
                            supplier_real: item.supplier_real || '',
                            weight: item.weight || 0.0,
                            delivery_days: item.delivery_days || 0,
                            delivery_type: item.delivery_type || ''
                          })} 
                          className="mt-4 bg-[#ff6600] hover:bg-[#e65c00] text-white text-sm font-bold py-2 px-6 rounded-lg transition-colors hidden lg:block active:scale-95"
                        >
                          У кошик
                        </button>     
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SearchPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-xl text-gray-500">Завантаження...</div>}>
      <SearchContent />
    </Suspense>
  );
}
